import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/styles";
import clsx from "clsx";
import {
  Grid,
  Typography,
  CircularProgress,
  Dialog,
  IconButton,
  DialogContent,
} from "@material-ui/core";
import { red } from "@material-ui/core/colors";
import * as Actions from "../../store/action/fileManagement";
import PhotoCameraIcon from "@material-ui/icons/PhotoCamera";
import CloseIcon from "@material-ui/icons/Close";
import Stack from "@mui/material/Stack";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { RiDeleteBin6Line } from "react-icons/ri";
import { TailSpin } from "react-loader-spinner";
import { hasAuthorities } from "../../utils/authoritiesUtils";
import { updateStatusClaim } from "../../store/action/claim";
import { getNextClaimStatus, hasDocumentType } from "../../utils/claimStatusProgress";
import { addEventLog } from "../../store/action/claimManagement";
import { UploadProgressDialog, UploadErrorDialog } from "../UploadProgressDialog";

const PICTURE_EVENT_LABELS = {
  PICTURE_BEFORE_REPARATION: "Ajout photos avant réparation",
  PICTURE_AFTER_REPARATION: "Ajout photos après réparation",
};

const useStyles = makeStyles((theme) => ({
  title: {
    marginLeft: 30,
    fontFamily: "Inter",
    fontSize: 16,
    fontWeight: 600,
    color: "#30385A",
  },
  subTitle: {
    marginLeft: 30,
    fontFamily: "Poppins",
    fontSize: 14,
    fontWeight: 400,
    color: "#002226B2",
  },
  ImageDeleteIcon: {
    position: "absolute",
    top: 0,
    right: 0,
    color: red[500],
    opacity: 0,
    visibility: "hidden",
    transition: "opacity 0.2s ease-in-out",
    backgroundColor: "#fff",
  },
  imageHoverContainer: {
    "&:hover $ImageDeleteIcon": {
      opacity: 1,
      visibility: "visible",
    },
  },
  justifyWithPadding: {
    padding: "10px",
    overflowY: "auto",
  },
  productImageUpload: {
    borderRadius: "7px",
    border: "1px solid #3C6556",
    borderColor: "#BBC808",
    width: "130px",
    height: "135px",
    background: "#F9F9F9",
  },
  productImageItem: {
    border: "1px solid #002226B2",
  },
  formGroup: {
    display: "flex",
    alignItems: "center",
    position: "relative",
    borderRadius: 2,
    padding: "12px 12px 0 12px",
    margin: "26px 12px 6px 12px",
  },
  formGroupTitle: {
    position: "absolute",
    top: -10,
    fontFamily: "Inter",
    fontSize: 16,
    fontWeight: 500,
    color: "#464E5F",
    padding: "0 4px",
  },
  previewDialog: {
    "& .MuiDialog-paper": {
      maxWidth: "90vw",
      maxHeight: "90vh",
      margin: "16px",
      backgroundColor: "#fff",
      borderRadius: "8px",
    },
  },
  previewContent: {
    position: "relative",
    padding: "16px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#f5f5f5",
  },
  previewImage: {
    maxWidth: "100%",
    maxHeight: "80vh",
    objectFit: "contain",
  },
  closeButton: {
    position: "absolute",
    right: 8,
    top: 8,
    color: "#666",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.9)",
    },
  },
}));

// -------------------- Image Preview Modal --------------------
const ImagePreviewModal = ({ open, onClose, image }) => {
  const classes = useStyles();
  const [imgSrc, setImgSrc] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!open || !image) return;

    const fetchImage = async () => {
      setIsLoading(true);
      try {
        const res = await fetch(image.src, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        if (!res.ok) throw new Error("Failed to fetch image");
        const blob = await res.blob();
        setImgSrc(URL.createObjectURL(blob));
      } catch (error) {
        console.error("Error fetching preview image:", error);
        toast.error("Failed to load image preview");
      } finally {
        setIsLoading(false);
      }
    };

    fetchImage();
    return () => {
      if (imgSrc) URL.revokeObjectURL(imgSrc);
    };
  }, [open, image]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={classes.previewDialog}
      maxWidth="xl"
      fullWidth
    >
      <DialogContent className={classes.previewContent}>
        <IconButton
          className={classes.closeButton}
          onClick={onClose}
          size="small"
        >
          <CloseIcon />
        </IconButton>

        {isLoading ? (
          <CircularProgress style={{ color: "#BBC808" }} />
        ) : (
          <img
            src={imgSrc}
            alt={image?.alt || "Preview"}
            className={classes.previewImage}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

// -------------------- Secure Image --------------------
const SecureImage = ({ src, alt, style }) => {
  const [imgSrc, setImgSrc] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchImage = async () => {
      if (!src) return;
      setIsLoading(true);
      setError(false);
      try {
        const res = await fetch(src, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        if (!res.ok) throw new Error("Failed to fetch image");
        const blob = await res.blob();
        if (isMounted) {
          setImgSrc(URL.createObjectURL(blob));
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error fetching image:", error);
        if (isMounted) {
          setError(true);
          setIsLoading(false);
          setImgSrc("");
        }
      }
    };
    fetchImage();
    return () => {
      isMounted = false;
      if (imgSrc) URL.revokeObjectURL(imgSrc);
    };
  }, [src]);

  return (
    <>
      <div
        style={{
          ...style,
          position: "relative",
          backgroundColor: "#F9F9F9",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        }}
        onClick={() => setPreviewOpen(true)}
      >
        {isLoading && (
          <CircularProgress
            size={24}
            style={{
              color: "#BBC808",
              position: "absolute",
              top: "50%",
              left: "50%",
              marginTop: -12,
              marginLeft: -12,
            }}
          />
        )}
        {error && (
          <div
            style={{
              color: "#666",
              fontSize: "12px",
              textAlign: "center",
              padding: "8px",
            }}
          >
            Failed to load image
          </div>
        )}
        {!isLoading && !error && (
          <img
            className="max-w-none w-auto h-full"
            src={imgSrc}
            alt={alt}
            style={{
              ...style,
              opacity: isLoading ? 0 : 1,
              transition: "opacity 0.3s ease-in-out",
            }}
          />
        )}
      </div>

      <ImagePreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        image={{ src, alt }}
      />
    </>
  );
};

// -------------------- Catalogue Modal --------------------
const CatalogueModal = ({ open, onClose, images, title, canDelete, onDelete }) => {
  const classes = useStyles();
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogContent
        style={{
          backgroundColor: "#fff",
          padding: "24px",
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: "12px",
        }}
      >
        <Typography
          variant="h6"
          style={{
            width: "100%",
            textAlign: "center",
            fontWeight: 600,
            color: "#30385A",
            marginBottom: "16px",
            fontFamily: "Poppins",
          }}
        >
          {title}
        </Typography>

        {images?.map((media, index) => (
          <div
            key={index}
            className={classes.imageHoverContainer}
            style={{
              width: "130px",
              height: "135px",
              border: "1px solid #ccc",
              borderRadius: "8px",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <SecureImage
              src={`${process.env.REACT_APP_BACKEND_URL_MS_FILEMANAGEMENT}${media.location}`}
              alt={media.fileName}
              style={{ width: "100%", height: "100%" }}
            />
            {canDelete && (
              <IconButton
                size="small"
                className={classes.ImageDeleteIcon}
                title="Supprimer"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(media.uid);
                }}
              >
                <RiDeleteBin6Line style={{ fontSize: 16 }} />
              </IconButton>
            )}
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
};

// -------------------- Main Component --------------------
function ImagesTab(props) {
  const classes = useStyles(props);
  const [ImageAfter, setImageAfter] = useState([]);
  const [ImageBefore, setImageBefore] = useState([]);
  const [ImageOther, setImageOther] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catalogue, setCatalogue] = useState({
    open: false,
    images: [],
    title: "",
  });
  const dispatch = useDispatch();
  const isTaskClaimed = props.isTaskClaimed;
  const renderUploadPicture = props.renderUploadPicture;
  const [refreshPicture, setRefreshPicture] = useState(false);
  const [loadingDialogOpen, setLoadingDialogOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadInfo, setUploadInfo] = useState({ current: 0, total: 0 });
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const updatedStatus = useRef(null);

  const handleUploadStart = (total) => {
    setLoadingDialogOpen(true);
    setUploadProgress(0);
    setUploadInfo({ current: 0, total });
  };

  const handleUploadProgress = (progress, current, total) => {
    setUploadProgress(progress);
    setUploadInfo({ current, total });
  };

  const handleUploadComplete = () => {
    setLoadingDialogOpen(false);
    setUploadProgress(0);
    setUploadInfo({ current: 0, total: 0 });
    setRefreshPicture((prev) => !prev);
  };

  const handleUploadError = (msg) => {
    setErrorMessage(msg);
    setErrorDialogOpen(true);
    setLoadingDialogOpen(false);
  };

  const fetchImageClaim = async () => {
    setLoading(true);
    // getAllFileForClaim relance l'erreur : sans capture, un 500 de searchFile
    // laissait le squelette de chargement indéfiniment et affichait l'overlay React
    let imageData;
    try {
      imageData = await dispatch(Actions.getAllFileForClaim(props?.claimNumber));
    } catch (error) {
      console.error("[fetchImageClaim]", error);
      setImageBefore([]);
      setImageAfter([]);
      setImageOther([]);
      if (typeof props.setTestImages === "function") {
        props.setTestImages(false);
      }
      setLoading(false);
      return;
    }
    const filteredImages = imageData?.reduce(
      (acc, file) => {
        if (
          file?.metadata.some(
            (metadata) => metadata?.value === "PICTURE_BEFORE_REPARATION"
          )
        ) {
          acc.before.push(file?.file);
        } else if (
          file?.metadata?.some(
            (metadata) => metadata?.value === "PICTURE_AFTER_REPARATION"
          )
        ) {
          acc.after.push(file?.file);
        } else if (
          file?.metadata?.some(
            (metadata) => metadata?.value === "OTHER_CAR_PICTURE"
          )
        ) {
          acc.other.push(file?.file);
        }
        return acc;
      },
      { before: [], after: [], other: [] }
    );
    setImageBefore(filteredImages?.before);
    setImageAfter(filteredImages?.after);
    setImageOther(filteredImages?.other);

    const hasImages = ["before", "after"].every(
      (key) => filteredImages?.[key]?.length > 0
    );
    if (typeof props.setTestImages === "function") {
      props.setTestImages(hasImages);
    }
    setLoading(false);

    // props.claimStatus n'est pas rafraîchi après la mise à jour : le ref garde le dernier statut écrit
    const currentStatus = updatedStatus.current || props.claimStatus;
    const nextStatus = getNextClaimStatus(currentStatus, {
      hasBefore: filteredImages?.before?.length > 0,
      hasAfter: filteredImages?.after?.length > 0,
      hasFinalReport: hasDocumentType(imageData, "FINAL_REPORT"),
    });
    if (nextStatus) {
      try {
        await dispatch(updateStatusClaim(props.claimNumber, nextStatus));
        updatedStatus.current = nextStatus;
        props.onStatusChange?.(nextStatus);
      } catch (error) {
        console.error("[updateStatusClaim]", error);
      }
    }
  };

  useEffect(() => {
    fetchImageClaim();
  }, [refreshPicture]);

  // delete actif uniquement en vue sinistre (enableDelete) — jamais dans les tâches todo
  const canDeletePhoto = !!props.enableDelete && hasAuthorities("DELETE_PHOTO");

  const handleDeleteImage = async (uid) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette photo ?")) return;
    try {
      await dispatch(Actions.deleteFileImage(uid));
      toast.success("Photo supprimée avec succès");
      setCatalogue({ open: false, images: [], title: "" });
      setRefreshPicture((prev) => !prev);
    } catch (error) {
      console.error("[handleDeleteImage]", error);
      toast.error("Échec de la suppression de la photo");
    }
  };

  const renderSection = (title, images, type, id) => {
    const displayedImages = images ? images.slice(0, 7) : [];
    const totalImages = images?.length || 0;
    const hasImages = totalImages > 0;

    return (
    <Grid item xs={12}>
      <div className={classes.formGroup}>
        <Typography className={classes.formGroupTitle}>{title}</Typography>
        <div
          className={clsx(
            classes.justifyWithPadding,
            "flex justify-center sm:justify-start flex-wrap"
          )}
        >
          <Stack direction="row" justifyContent="flex-start" alignItems="stretch">
            {!renderUploadPicture && (
              <UploadButton
                isTaskClaimed={isTaskClaimed}
                claimNumber={props.claimNumber}
                type={type}
                isFirstUpload={totalImages === 0}
                id={id}
                onUploadStart={handleUploadStart}
                onUploadProgress={handleUploadProgress}
                onUploadComplete={handleUploadComplete}
                onUploadError={handleUploadError}
              />
            )}

            {displayedImages.map((media) => (
                <div
                  className={clsx(
                    classes.productImageItem,
                    classes.imageHoverContainer,
                    "flex items-center justify-center relative h-128 rounded-4 mr-16 mb-16 overflow-hidden cursor-pointer shadow-1 hover:shadow-5"
                  )}
                  key={media.uid}
                  style={{
                    width: "130px",
                    height: "135px",
                    borderRadius: "7px",
                    marginLeft: "5px",
                    display: "inline-block",
                    position: "relative",
                  }}
                >
                  <SecureImage
                    src={`${process.env.REACT_APP_BACKEND_URL_MS_FILEMANAGEMENT}${media.location}`}
                    alt={media.fileName}
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "7px",
                    }}
                  />
                  {canDeletePhoto && (
                    <IconButton
                      size="small"
                      className={classes.ImageDeleteIcon}
                      title="Supprimer"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteImage(media.uid);
                      }}
                    >
                      <RiDeleteBin6Line style={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </div>
              ))}

            {Array.from({ length: Math.max(0, 7 - displayedImages.length) }).map(
              (_, index) => (
                <div
                  key={index}
                  style={{
                    width: "130px",
                    height: "135px",
                    backgroundColor: "#F9F9F9",
                    borderRadius: "7px",
                    marginLeft: "1%",
                    marginRight: index < 7 ? "5px" : 0,
                  }}
                ></div>
              ))}

            {hasImages && (
              <div
                style={{
                  width: "130px",
                  height: "135px",
                  borderRadius: "7px",
                  marginLeft: "1%",
                  backgroundColor: "#EAEAEA",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  position: "relative",
                  cursor: "pointer",
                }}
                onClick={() =>
                  setCatalogue({ open: true, images, title })
                }
              >
               <Typography
  variant="body2"
  style={{
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "#BBC808",
    color: "#fff",
    borderRadius: "4px",
    padding: "4px 10px",
    fontSize: "12px",
    fontWeight: 500,
    fontFamily: "'Poppins', sans-serif",
    textAlign: "center",
  }}
>
  Voir tout
  
  </Typography>

              </div>
            )}
          </Stack>
        </div>
      </div>
    </Grid>
    );
  };

  return (
    <React.Fragment>
      <Grid container>
        {renderSection("Avant réparation", ImageBefore, "PICTURE_BEFORE_REPARATION", "UBAvant")}
        {renderSection("Après réparation", ImageAfter, "PICTURE_AFTER_REPARATION", "UBApres")}
        {renderSection("Autres", ImageOther, "OTHER_CAR_PICTURE", "UBAutres")}
      </Grid>

      <CatalogueModal
        open={catalogue.open}
        onClose={() => setCatalogue({ open: false, images: [], title: "" })}
        images={catalogue.images}
        title={catalogue.title}
        canDelete={canDeletePhoto}
        onDelete={handleDeleteImage}
      />

      <UploadProgressDialog
        open={loadingDialogOpen}
        progress={uploadProgress}
        current={uploadInfo.current}
        total={uploadInfo.total}
      />
      <UploadErrorDialog
        open={errorDialogOpen}
        message={errorMessage}
        onClose={() => setErrorDialogOpen(false)}
      />
    </React.Fragment>
  );
}

// -------------------- Upload Button --------------------
function UploadButton(props) {
  const classes = useStyles(props);
  const dispatch = useDispatch();
  const isTaskClaimed = props.isTaskClaimed;
  const [isUploading, setIsUploading] = useState(false);
  const currentUser = localStorage.getItem("username");

  function handleUpload(e) {
    if (e.target.files.length === 0) return;
    const validImageTypes = ["image/jpeg", "image/png", "image/jpg"];
    const invalidFiles = Array.from(e.target.files).filter(
      (file) => !validImageTypes.includes(file.type)
    );
    if (invalidFiles.length > 0) {
      props.onUploadError("Seuls les fichiers image (JPEG, PNG, JPG) sont acceptés.");
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    const oversizedFiles = Array.from(e.target.files).filter(
      (file) => file.size > maxSize
    );
    if (oversizedFiles.length > 0) {
      props.onUploadError("Certains fichiers sont trop volumineux (max 5MB par fichier).");
      return;
    }

    const totalFiles = e.target.files.length;
    setIsUploading(true);
    props.onUploadStart(totalFiles);

    dispatch(
      Actions.postImageMs_filemanagement(
        e.target.files,
        props.type,
        props.claimNumber,
        currentUser,
        (progress, current) => props.onUploadProgress(progress, current, totalFiles)
      )
    )
      .then(async () => {
        const eventLabel = PICTURE_EVENT_LABELS[props.type];
        if (eventLabel && props.isFirstUpload) {
          try {
            await addEventLog(props.claimNumber, eventLabel, currentUser);
          } catch (error) {
            console.error("[addEventLog]", error);
          }
        }
        props.onUploadComplete();
      })
      .catch((error) => {
        console.error("Error uploading image: ", error);
        props.onUploadError("Une erreur est survenue lors de l'ajout de la photo");
      })
      .finally(() => {
        setIsUploading(false);
        e.target.value = "";
      });
  }

  return (
    <div className="mr-16 mb-16 mt-0">
      <label
        htmlFor={props.id}
        className={clsx(
          classes.productImageUpload,
          "flex rounded-4 cursor-pointer shadow-1 hover:shadow-5",
          "text-center"
        )}
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <input
          disabled={isTaskClaimed || isUploading}
          accept="image/*"
          id={props.id}
          type="file"
          onChange={(event) => handleUpload(event)}
          multiple
          style={{ border: 0, opacity: 0 }}
        />
        {isUploading ? (
          <>
            <TailSpin color="#BBC808" height={35} width={35} />
            <span
              style={{
                fontFamily: "Poppins",
                fontSize: "14px",
                fontWeight: 400,
                color: "#002226",
                marginTop: "8px",
              }}
            >
              Chargement...
            </span>
          </>
        ) : (
          <>
            <PhotoCameraIcon
              fontSize="large"
              style={{
                width: "35px",
                height: "28.44px",
                color: "#BBC808",
              }}
            />
            <span
              style={{
                fontFamily: "Poppins",
                fontSize: "14px",
                fontWeight: 400,
                color: "#002226",
                marginTop: "8px",
              }}
            >
              Ajouter photo
            </span>
          </>
        )}
      </label>
    </div>
  );
}

export default ImagesTab;
